import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { MockApiService } from '../../mocks/mock-api.service';

@Injectable({
  providedIn: 'root',
})

export class RestApi {

  authUrl: string = "https://lancaster-bus-app.fra0.kraft.host/api/auth";
  apiUrl: string;
  refreshToken: string;
  authToken: string;
  tokenExpiry: string;
  reqHeaders: HttpHeaders;
  private inFlightAuthRequest?: Promise<ApiResponse>;
  private inFlightBusDataRequests = new Map<string, Promise<ApiResponse>>();


  constructor(
    private http: HttpClient,
    private mockApi: MockApiService
  ) {
    this.apiUrl = 'https://itpiapi.lancs.ac.uk';
  }


  public async get(endpoint: string, retrying = false) {
    const mockBusDataResponse = await this.interceptMockBusDataRequest(endpoint, retrying);
    if (mockBusDataResponse) {
      if (mockBusDataResponse.status === 401 && !retrying) {
        await this.getAuthCredentials();
        return this.get(endpoint, true);
      }
      return mockBusDataResponse;
    }

    let headers = await this.fetchReqHeaders();
    try {
      let data = await this.http.get(this.apiUrl + this.withToken(endpoint), { headers }).toPromise();
      return new ApiResponse(200, 'success', true, data);
    }
    catch (err) {
      if (err['status'] === 401 && !retrying) {
        await this.getAuthCredentials();
        return this.get(endpoint, true);
      }
      return this.handleError(err);
    }
  }

  public async post(endpoint, body, retrying = false) {
    let headers = await this.fetchReqHeaders();
    try {
      let data = await this.http.post(this.apiUrl + this.withToken(endpoint), body, { headers }).toPromise();
      return new ApiResponse(200, 'success', true, data);
    }
    catch (err) {
      if (err['status'] === 401 && !retrying) {
        await this.getAuthCredentials();
        return this.post(endpoint, body, true);
      }
      return this.handleError(err);
    }
  }

  public handleError(err) {
    // Ajax call cancelled
    if (err['status'] === 0) {
      let errors = [{ 'message': "Can't connect to server. Please try again later." }];
      return new ApiResponse(0, 'cancelled', false, errors);
    }
    let errors = (err && err.error && err.error.errors) ? err.error.errors : [{ message: err['message'] || 'Unknown error' }];
    return new ApiResponse(err.status, err.statusText, false, errors);
  }


  private async fetchReqHeaders() {
    let authToken = await this.authToken;

    if (!authToken) {
      await this.getAuthCredentials();
    }
    return new HttpHeaders({
      Authorization: `Bearer ${this.authToken}`
    });
  }

  private withToken(endpoint: string) {
    let separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}token=${this.authToken}`;
  }


  // Get authentication credentials and store in cache
  public async getAuthCredentials() {
    if (this.inFlightAuthRequest) {
      return this.inFlightAuthRequest;
    }

    this.inFlightAuthRequest = (async () => {
      const mockAuthResponse = await this.interceptMockAuthRequest();
      if (mockAuthResponse) {
        if (mockAuthResponse.ok) {
          this.authToken = mockAuthResponse.body['token'];
          this.refreshToken = mockAuthResponse.body['RefreshToken'];
          this.tokenExpiry = mockAuthResponse.body['experatinTime'];
        }
        return mockAuthResponse;
      }

      try {
        let data = await this.http.get(this.authUrl).toPromise();
        this.authToken = data['token'];
        this.refreshToken = data['RefreshToken'];
        this.tokenExpiry = data['experatinTime'];
        return new ApiResponse(200, 'success', true, data);
      }
      catch (err) {
        return this.handleError(err);
      }
    })();

    this.inFlightAuthRequest.finally(() => {
      if (this.inFlightAuthRequest) {
        this.inFlightAuthRequest = undefined;
      }
    });

    return this.inFlightAuthRequest;
  }

  /*
  ########################## 
  GET requests
  ########################## 
 */


  // Get all active contract templates with its descripiton and plans
  public async getBusData(busstop = "LANCBS001", limit = 200) {
    const endpoint = '/transport/api/v1/BusStops/' + busstop + '/next?limit=' + limit;
    const requestKey = `${busstop}::${limit}`;

    const existingRequest = this.inFlightBusDataRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const requestPromise = (async () => {
      if (environment.enableMockTools && this.mockApi.shouldMockBusData()) {
        return this.mockApi.getBusData(limit);
      }
      return this.get(endpoint);
    })();

    this.inFlightBusDataRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => {
      if (this.inFlightBusDataRequests.get(requestKey) === requestPromise) {
        this.inFlightBusDataRequests.delete(requestKey);
      }
    });

    return requestPromise;
  }

  private async interceptMockBusDataRequest(endpoint: string, retrying: boolean): Promise<ApiResponse | null> {
    if (!environment.enableMockTools || !endpoint.startsWith('/transport/api/v1/BusStops/')) {
      return null;
    }

    return this.mockApi.interceptBusDataRequest(retrying);
  }

  private async interceptMockAuthRequest(): Promise<ApiResponse | null> {
    if (!environment.enableMockTools) {
      return null;
    }

    return this.mockApi.interceptAuthRequest();
  }

  // Stagecoach Bus Screens Twitter feed
  public async getTwitterStagecoachCNL() {
    return await this.get('/production/api/SocialMedia/Twitter/StagecoachCNL');
  }

  // Lancaster University Bus Screens Twitter feed
  public async getTwitterLUBusScreens() {
    return await this.get('/production/api/SocialMedia/Twitter/LUBusScreens');
  }

  //  /*
  //  ########################## 
  //  POST requests
  //  ########################## 
  // */
  //  /**
  //   * Send a POST request with username and pw to our login endpoint with the data
  //   * the user entered on the form. Processed in providers/users
  //   * + email : string
  //   * +passowrd: string
  //   * + notification_token : string (firebase token)
  //  */
  //  public async login(email, password, notification_token) {
  //    let body = {};
  //    body["email"] = email;
  //    body["password"] = password;
  //    body["notification_token"] = notification_token;
  //    return await this.post('/apiv2/account/login', body);
  //  }
  //    
  //  /**
  //   * Send a POST request with all data captured in form for
  //   * registration.
  //  */    
  //  public async register(body) {
  //    return await this.post('/apiv2/account/register', body);
  //  }   
}

export class ApiResponse {
  public status: number;
  public statusText: string;
  public ok: boolean;
  public body: any;
  public errors: any;

  constructor(status: number, statusText: string, ok: boolean, payload: any) {
    this.status = status;
    this.statusText = statusText;
    this.ok = ok;
    if (ok) {
      this.body = payload,
        this.errors = null;
    }
    else {
      this.body = null,
        this.errors = payload;
    }
  }
  public static renderErrorMessage(errors) {
    console.log(errors);
    try {
      let message: string = '';
      for (var element of errors) {
        if ('field' in element) {
          let field = element['field'].charAt(0).toUpperCase() + element['field'].slice(1);
          message += field + ": " + element['message'] + "";
        }
        else {
          message += element['message'] + " ";
        }
      };
      return message;
    }

    catch (e) {
      return "Unknown error. Please try again later.";
    }
  }
}
